if (!customElements.get('wi-product-card')) {
  customElements.define(
    'wi-product-card',
    class WIProductCard extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.variantData = this.parseVariantData();
        this.optionButtons = Array.from(this.querySelectorAll('[data-wi-option-button]'));
        this.variantInput = this.querySelector('[data-wi-variant-id]');
        this.submitButton = this.querySelector('[data-wi-submit]');
        this.priceElement = this.querySelector('[data-wi-price]');
        this.compareElement = this.querySelector('[data-wi-compare]');
        this.discountElement = this.querySelector('[data-wi-discount]');
        this.imageElement = this.querySelector('[data-wi-image]');
        this.variantBadge = this.querySelector('[data-wi-variant-badge]');
        this.productLinks = Array.from(this.querySelectorAll('[data-wi-product-link]'));

        this.optionButtons.forEach((button) => {
          button.addEventListener('click', () => this.handleOptionClick(button));
        });

        const currentVariantId = Number(this.variantInput?.value);
        const currentVariant = this.variantData.find((variant) => variant.id === currentVariantId);
        if (currentVariant) this.updateCard(currentVariant, false);
        this.updateOptionAvailability();
      }

      parseVariantData() {
        try {
          return JSON.parse(this.querySelector('[data-wi-variants]')?.textContent || '[]');
        } catch (error) {
          console.error('WI product card: invalid variant data', error);
          return [];
        }
      }

      handleOptionClick(button) {
        if (button.getAttribute('aria-disabled') === 'true') return;

        const position = button.dataset.optionPosition;
        this.optionButtons
          .filter((item) => item.dataset.optionPosition === position)
          .forEach((item) => {
            const selected = item === button;
            item.classList.toggle('is-selected', selected);
            item.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });

        const selectedOptions = this.getSelectedOptions();
        let matchingVariant = this.variantData.find((variant) =>
          variant.options.every((value, index) => value === selectedOptions[index]),
        );

        if (!matchingVariant) {
          const clickedIndex = Number(button.dataset.optionPosition) - 1;
          matchingVariant =
            this.variantData.find(
              (variant) => variant.available && variant.options[clickedIndex] === button.dataset.optionValue,
            ) || this.variantData.find((variant) => variant.options[clickedIndex] === button.dataset.optionValue);
          if (matchingVariant) this.syncSelectedButtons(matchingVariant);
        }

        if (matchingVariant) this.updateCard(matchingVariant, true);
        this.updateOptionAvailability();
      }

      getSelectedOptions() {
        const selected = [];
        this.optionButtons
          .filter((button) => button.classList.contains('is-selected'))
          .forEach((button) => {
            selected[Number(button.dataset.optionPosition) - 1] = button.dataset.optionValue;
          });
        return selected;
      }

      syncSelectedButtons(variant) {
        this.optionButtons.forEach((button) => {
          const optionIndex = Number(button.dataset.optionPosition) - 1;
          const selected = variant.options[optionIndex] === button.dataset.optionValue;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
      }

      updateOptionAvailability() {
        if (!this.optionButtons.length) return;

        this.optionButtons.forEach((button) => {
          const optionIndex = Number(button.dataset.optionPosition) - 1;
          const possible = this.variantData.some(
            (variant) => variant.available && variant.options[optionIndex] === button.dataset.optionValue,
          );

          button.classList.toggle('is-unavailable', !possible);
          button.setAttribute('aria-disabled', possible ? 'false' : 'true');
        });
      }

      updateCard(variant, announceChange) {
        if (this.variantInput) {
          this.variantInput.value = variant.id;
          this.variantInput.disabled = !variant.available;
          if (announceChange) this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this.priceElement) this.priceElement.textContent = variant.price;

        const hasDiscount = Number(variant.compareAtRaw) > Number(variant.priceRaw);
        if (this.compareElement) {
          this.compareElement.textContent = hasDiscount ? variant.compareAt || '' : '';
          this.compareElement.classList.toggle('is-hidden', !hasDiscount);
        }

        if (this.discountElement) {
          const percentage = hasDiscount
            ? Math.round(((Number(variant.compareAtRaw) - Number(variant.priceRaw)) / Number(variant.compareAtRaw)) * 100)
            : 0;
          this.discountElement.textContent = hasDiscount ? `(${percentage}% OFF)` : '';
          this.discountElement.classList.toggle('is-hidden', !hasDiscount);
        }

        if (this.imageElement && variant.image) {
          this.imageElement.src = variant.image;
          this.imageElement.removeAttribute('srcset');
          this.imageElement.alt = variant.imageAlt || this.imageElement.alt;
        }

        if (this.variantBadge) this.variantBadge.textContent = variant.title;
        this.productLinks.forEach((link) => {
          link.href = variant.url;
        });

        if (this.submitButton) {
          const label = this.submitButton.querySelector('[data-wi-cta-label]');
          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          const addLabel = this.submitButton.dataset.addLabel || 'Add to cart';
          const soldOutLabel = this.submitButton.dataset.soldOutLabel || 'Sold out';
          this.submitButton.disabled = !variant.available;
          this.submitButton.toggleAttribute('aria-disabled', !variant.available);
          if (label) {
            label.textContent = variant.available ? addLabel : soldOutLabel;
            label.classList.remove('hidden');
          }
          soldOutMessage?.classList.add('hidden');
        }
      }
    },
  );
}
